import { memo, useCallback, useMemo } from 'react';

import { SegmentedControl, type SegmentedControlOption } from '@air/component-segmented-control';
import { LayoutGridIcon, ListIcon } from '@air/design-icons';

export type ListViewMode = 'gallery' | 'list';

export type ListViewModeToggleProps = {
  appearance?: 'pill' | 'square';
  'data-testid'?: string;
  galleryTestId?: string;
  listTestId?: string;
  mode: ListViewMode;
  onModeChange: (mode: ListViewMode) => void;
};

const isListViewMode = (value: string): value is ListViewMode => value === 'gallery' || value === 'list';

export const ListViewModeToggle = memo(
  ({
    appearance = 'square',
    'data-testid': dataTestId = 'list-view-mode-toggle',
    galleryTestId = 'list-view-mode-gallery',
    listTestId = 'list-view-mode-list',
    mode,
    onModeChange,
  }: ListViewModeToggleProps) => {
    const options = useMemo<SegmentedControlOption[]>(
      () => [
        {
          id: 'gallery',
          label: <LayoutGridIcon aria-hidden="true" size="xs" />,
          testId: galleryTestId,
          tooltip: 'Gallery view',
          value: 'gallery',
        },
        {
          id: 'list',
          label: <ListIcon aria-hidden="true" size="xs" />,
          testId: listTestId,
          tooltip: 'List view',
          value: 'list',
        },
      ],
      [galleryTestId, listTestId],
    );

    const selectedOption = useMemo(
      () => options.find((option) => option.value === mode) ?? options[0],
      [mode, options],
    );

    const handleSelect = useCallback(
      (option: SegmentedControlOption) => {
        if (isListViewMode(option.value)) {
          onModeChange(option.value);
        }
      },
      [onModeChange],
    );

    const isPillAppearance = appearance === 'pill';

    return (
      <SegmentedControl
        appearance={appearance}
        color="gray"
        data-testid={dataTestId}
        includeTooltip
        onSelect={handleSelect}
        options={options}
        outerAppearance={isPillAppearance ? 'pill' : 'default'}
        selectedOption={selectedOption}
        size="small"
      />
    );
  },
);

ListViewModeToggle.displayName = 'ListViewModeToggle';
